import React, { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading } from "@chakra-ui/react";
import axios from "axios";
import echarts from "./lib";

// Brand hues (brightened ramp — see Chart1, the reference implementation).
const TEAL = "#14A3B2";
const TEAL_DEEP = "#0E7C86";
const AMBER = "#E8940F";

const fmtShort = (value) => {
  if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
  return `R${value.toLocaleString("en-US")}`;
};

const lighten = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift) => {
    const c = (n >> shift) & 0xff;
    return Math.round(c + (255 - c) * amt);
  };
  return `rgb(${ch(16)}, ${ch(8)}, ${ch(0)})`;
};

const barGradient = (from, to) => ({
  type: "linear",
  x: 0,
  y: 0,
  x2: 1,
  y2: 0,
  colorStops: [
    { offset: 0, color: from },
    { offset: 1, color: to },
  ],
});

const Chart2 = ({ supplier, retailer }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let disposed = false;

    const initChart = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart2`,
          {
            supplier,
            retailer,
            userid: localStorage.getItem("userid"),
          }
        );

        // Identical transform to the AmCharts version — the DATA must not
        // change in this migration; only the presentation does.
        const data = res.data.data.map((item) => ({
          region: item.region,
          value: Math.round(item.value),
        }));

        if (disposed || !containerRef.current) return;

        const total = data.reduce((sum, d) => sum + d.value, 0);
        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;

        // Click any bar to flip between the API's order and value-sorted.
        let sortedByValue = false;

        const render = () => {
          const rows = sortedByValue
            ? [...data].sort((a, b) => b.value - a.value)
            : data;
          const max = Math.max(...rows.map((r) => r.value), 1);

          chart.setOption(
            {
              title: {
                subtext: sortedByValue
                  ? "sorted by value — click a bar for original order"
                  : "click a bar to sort by value",
                right: 0,
                top: 0,
                subtextStyle: { color: "#93A5B1", fontSize: 10.5 },
              },
              grid: { left: 8, right: 44, top: 20, bottom: 4, containLabel: true },
              xAxis: {
                type: "value",
                axisLabel: { color: "#64798A", fontSize: 11, formatter: fmtShort },
                splitLine: { lineStyle: { color: "#E2E9EE", type: [3, 3] } },
                axisLine: { show: false },
                axisTick: { show: false },
              },
              yAxis: {
                type: "category",
                inverse: true, // first API row stays on top, as before
                data: rows.map((r) => r.region),
                axisLabel: {
                  color: "#4A5D6B",
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                  width: 120,
                  overflow: "break",
                },
                axisLine: { show: false },
                axisTick: { show: false },
              },
              tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow", shadowStyle: { color: "rgba(20,163,178,0.06)" } },
                confine: true,
                backgroundColor: "#ffffff",
                borderColor: "#E2E9EE",
                borderWidth: 1,
                padding: [8, 12],
                textStyle: { color: "#1D2F3A", fontSize: 12 },
                extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
                formatter: (params) => {
                  const p = params[0];
                  const share = total ? ((100 * p.value) / total).toFixed(1) : "0.0";
                  return `${p.name}<br/><b>R${p.value.toLocaleString("en-US")}</b> — ${share}% of all regions`;
                },
              },
              series: [
                {
                  type: "bar",
                  barWidth: "68%",
                  showBackground: true,
                  backgroundStyle: { color: "#F6F8FA", borderRadius: [0, 6, 6, 0] },
                  data: rows.map((r) => {
                    const inside = r.value / max >= 0.7;
                    return {
                      value: r.value,
                      label: {
                        show: true,
                        position: inside ? "insideRight" : "right",
                        distance: 8,
                        color: inside ? "#ffffff" : "#4A5D6B",
                        ...(inside
                          ? { textShadowColor: "rgba(0,0,0,0.28)", textShadowBlur: 3 }
                          : {}),
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: "Inter, sans-serif",
                        formatter: (p) => fmtShort(p.value),
                      },
                    };
                  }),
                  itemStyle: {
                    borderRadius: [0, 6, 6, 0],
                    color: barGradient(lighten(TEAL, 0.35), TEAL_DEEP),
                  },
                  emphasis: {
                    itemStyle: {
                      color: barGradient(lighten(AMBER, 0.3), AMBER),
                      shadowBlur: 10,
                      shadowColor: "rgba(29,47,58,0.22)",
                    },
                  },
                  animationDuration: 900,
                  animationDelay: (idx) => idx * 70,
                  animationEasing: "cubicOut",
                  animationDurationUpdate: 500,
                },
              ],
            },
            { replaceMerge: ["series"] }
          );
        };

        render();
        chart.on("click", { seriesIndex: 0 }, () => {
          sortedByValue = !sortedByValue;
          render();
        });
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    initChart();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer]);

  return (
    <Box height="100%" display="flex" flexDirection="column" position="relative">
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        Sales by Region
      </Heading>
      <Box flex="1" position="relative">
        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", overflow: "hidden" }}
        ></div>
        {loading && (
          <Spinner
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
        )}
      </Box>
    </Box>
  );
};

export default Chart2;
